import { Component, OnInit } from '@angular/core';
import { AlertController, ModalController, NavParams } from '@ionic/angular';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { FunctionsService } from 'src/app/services/functions/functions.service';
import { BrickPreviewComponent } from '../brick-preview/brick-preview.component';
import generateTicket from 'src/app/shared/ticket';
import { lineName, lineVariant } from 'src/app/shared/event-products/product-variants';
import { NO_COMPANY_LABEL, courtesyCompany, isCourtesyOrder } from 'src/app/shared/courtesy/courtesy.model';

/**
 * Detalle de una orden de evento (monitor-v3).
 *
 * El OrderComponent genérico está pensado para el delivery de tienda: mapa,
 * repartidor, número de factura, splits de amigos… En un evento nada de eso
 * aplica. Aquí lo que importa es qué se vendió, cómo se pagó, desde qué caja
 * y en qué zona; la mesa o fila solo aparece cuando la orden la trae.
 */
@Component({
    selector: 'app-event-order',
    templateUrl: './event-order.component.html',
    styleUrls: ['./event-order.component.scss'],
})
export class EventOrderComponent implements OnInit {

    order: any;
    zoneSelected: any;
    event_name: string = '';

    /** Nombre del brick de mesa, cuando el evento vende mesas. */
    table_name: string = '';
    /** true mientras se cambia el estado, para no disparar dos veces. */
    advancing: boolean = false;

    readonly PAYMENTS: any = {
        cash:     { label: 'Efectivo', icon: 'cash-outline' },
        card:     { label: 'Tarjeta', icon: 'card-outline' },
        pos:      { label: 'POS', icon: 'calculator-outline' },
        datafono: { label: 'Datáfono', icon: 'calculator-outline' },
        btc:      { label: 'Bitcoin', icon: 'logo-bitcoin' },
        strike:   { label: 'Strike', icon: 'flash-outline' },
        pse:      { label: 'PSE', icon: 'business-outline' },
        credit:   { label: 'Crédito', icon: 'wallet-outline' },
        courtesy: { label: 'Regalía', icon: 'gift-outline' },
    };

    /**
     * El flujo de estados es el mismo que en tienda (pending → making → way →
     * delivered); solo cambia el vocabulario: en un evento no hay "en camino",
     * la orden queda lista en la barra.
     */
    readonly STEPS: any[] = [
        { status: 'pending',   label: 'Recibida',       icon: 'receipt-outline' },
        { status: 'making',    label: 'En preparación', icon: 'flame-outline' },
        { status: 'way',       label: 'Lista',          icon: 'notifications-outline' },
        { status: 'delivered', label: 'Entregada',      icon: 'checkmark-done-outline' },
    ];

    constructor(
        public api: ApiService,
        public auth: AuthService,
        public navParams: NavParams,
        public modalController: ModalController,
        private functionsService: FunctionsService,
        private alertController: AlertController,
    ) {
        this.order = navParams.get('order') || {};
        this.zoneSelected = navParams.get('zoneSelected');
        this.event_name = navParams.get('event_name') || '';
    }

    async ngOnInit() {
        // La página ya resuelve la cuenta, pero si llega la llave se completa.
        if (typeof this.order.account === 'string') {
            const data = await this.api.getDocument('accounts', this.order.account);
            this.order.account = data || { name: 'Cliente' };
        }
        if (!this.order.account) this.order.account = {};

        if (this.order.event_brick_table_aux && this.order.event_brick_table_aux.name) {
            this.table_name = this.firstLine(this.order.event_brick_table_aux.name);
        } else if (typeof this.order.event_brick_table === 'string' && this.order.event_brick_table) {
            const data = await this.api.getDocument('bricks', this.order.event_brick_table);
            if (data && data['name']) this.table_name = this.firstLine(data['name']);
        } else if (this.order.event_brick_table && this.order.event_brick_table.name) {
            this.table_name = this.firstLine(this.order.event_brick_table.name);
        }
    }

    // ── Lecturas ────────────────────────────────────────────────────────────

    get zone_name(): string {
        return (this.zoneSelected && this.zoneSelected.name) ? this.zoneSelected.name : '';
    }

    get status(): string {
        return this.order.status || 'pending';
    }

    get is_canceled(): boolean {
        return this.status === 'canceled';
    }

    get is_delivered(): boolean {
        return this.status === 'delivered';
    }

    /** Índice del paso actual dentro de STEPS (-1 si está cancelada). */
    get step_index(): number {
        return this.STEPS.findIndex(step => step.status === this.status);
    }

    get status_label(): string {
        if (this.is_canceled) return 'Cancelada';
        const step = this.STEPS[this.step_index];
        return step ? step.label : this.status;
    }

    /** Fecha de creación como Date, venga como Date o como Timestamp. */
    get added_date(): Date {
        return this.toDate(this.order.added_date);
    }

    /** Momento en que la orden llegó a su estado actual, si quedó registrado. */
    get status_date(): Date {
        const times = this.order.status_time || {};
        return this.toDate(times[this.status]);
    }

    get payment_type(): string {
        // Regalía (total 0) aunque venga guardada como efectivo o POS de antes.
        if (this.is_courtesy) return 'courtesy';
        return (this.order.payment && this.order.payment.type) ? this.order.payment.type : '';
    }

    // ── Regalías ────────────────────────────────────────────────────────────

    /** Orden sin cobro: lo que lleva se regaló (ver shared/courtesy). */
    get is_courtesy(): boolean {
        return isCourtesyOrder(this.order);
    }

    get courtesy_company(): string {
        return courtesyCompany(this.order);
    }

    get courtesy_company_label(): string {
        return this.courtesy_company || NO_COMPANY_LABEL;
    }

    /**
     * Cambiar (o completar) la empresa de una regalía: las anteriores a la
     * captura en caja no la traen, y un error de tecleo se corrige aquí sin
     * tocar la orden desde la consola.
     */
    async editCourtesyCompany() {
        if (!this.is_courtesy || !this.order.$key) return;
        const alert = await this.alertController.create({
            cssClass: 'alertStyle',
            header: 'Empresa de la regalía',
            message: `Orden #${this.order.number}: ¿a qué empresa se regaló?`,
            inputs: [{ name: 'company', type: 'text', value: this.courtesy_company, placeholder: 'Ej. Coca-Cola, Banco Agrícola, Staff…', attributes: { maxlength: 80, autocapitalize: 'words' } }],
            buttons: [
                { text: 'Cancelar', role: 'cancel' },
                {
                    text: 'Guardar',
                    handler: (data: any) => {
                        const company = String((data && data.company) || '').replace(/\s+/g, ' ').trim();
                        if (company.length < 2) return false;
                        // En cola: sin conexión se envía al volver la señal, como el resto de la caja.
                        this.api.updateDocumentQueued('orders', this.order.$key, { courtesy: true, courtesy_company: company });
                        this.order.courtesy = true;
                        this.order.courtesy_company = company;
                        return true;
                    },
                },
            ],
        });
        await alert.present();
    }

    get payment_label(): string {
        const found = this.PAYMENTS[this.payment_type];
        return found ? found.label : (this.payment_type || 'Sin registrar');
    }

    get payment_icon(): string {
        const found = this.PAYMENTS[this.payment_type];
        return found ? found.icon : 'help-circle-outline';
    }

    get card(): any {
        return (this.order.payment && this.order.payment.card) ? this.order.payment.card : null;
    }

    get is_cash(): boolean {
        return this.payment_type === 'cash';
    }

    get is_card(): boolean {
        return this.payment_type === 'card';
    }

    /** Pasarela con la que se cobró la tarjeta, si la orden lo trae. */
    get gateway(): string {
        const data = this.order.payments_data;
        if (!data || !data.typeTransaction) return '';
        if (data.typeTransaction === 'F1') return 'FAC - BAC';
        if (data.typeTransaction === 'P1') return 'Payway';
        return data.typeTransaction;
    }

    get credit_months(): string {
        const payment = this.order.payment || {};
        if (!payment.credit || !payment.credit_condition) return '';
        return String(payment.credit_condition).split('m')[0];
    }

    get cash_received(): number {
        return Number(this.order.change || 0);
    }

    get cash_change(): number {
        const diff = this.cash_received - this.total;
        return diff > 0 ? diff : 0;
    }

    get bricks(): any[] {
        return Array.isArray(this.order.bricks) ? this.order.bricks : [];
    }

    get items_count(): number {
        return this.bricks.reduce((sum, brick) => sum + Number(brick.quantity || 0), 0);
    }

    get total(): number {
        return Number(this.order.total || 0);
    }

    get tip(): number {
        return Number(this.order.tip || 0);
    }

    get cost_admin(): number {
        return Number(this.order.cost_admin || 0);
    }

    get delivery(): number {
        return Number(this.order.total_delivery || 0);
    }

    get discounts(): number {
        const o = this.order;
        const bin = (o.bin && o.bin.bin_cant_discount) ? Number(o.bin.bin_cant_discount) : 0;
        return Number(o.supplier_discount_total || 0)
            - Number(o.supplier_discount_not_applied || 0)
            + Number(o.second_round_discount || 0)
            + Number(o.all_store_discount || 0)
            + bin;
    }

    /** Lo que suman los productos antes de propina, cargos y descuentos. */
    get subtotal(): number {
        const fromBricks = this.bricks.reduce((sum, brick) => sum + this.lineTotal(brick), 0);
        if (fromBricks > 0) return fromBricks;
        return this.total - this.tip - this.cost_admin - this.delivery + this.discounts;
    }

    get note(): string {
        return this.order.note ? String(this.order.note).trim() : '';
    }

    get document(): string {
        const account = this.order.account || {};
        const value = this.order.dui || account.dui || '';
        return this.isPlaceholder(value) ? '' : value;
    }

    get phone(): string {
        const account = this.order.account || {};
        const value = this.order.phone || account.phone_complete || account.phone || '';
        return this.isPlaceholder(value) ? '' : value;
    }

    get customer_name(): string {
        const account = this.order.account || {};
        const name = [account.name, account.lastname].filter(part => !!part).join(' ').trim();
        return name || account.email || 'Cliente';
    }

    get customer_initials(): string {
        const parts = this.customer_name.split(' ').filter(part => !!part);
        return parts.slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('') || '•';
    }

    /** Las órdenes que nacen en el monitor las digita la caja; las demás vienen de la app. */
    get sold_at_box(): boolean {
        return this.order.systemOs === 'web' || this.order.sytemOs === 'web';
    }

    get row_code(): any {
        return this.order.event_row_code > 0 ? this.order.event_row_code : null;
    }

    get has_table(): boolean {
        return !!this.table_name || !!this.row_code;
    }

    /** Solo se puede avanzar el estado mientras la orden está viva. */
    get can_advance(): boolean {
        return !this.is_canceled && !this.is_delivered && this.step_index >= 0;
    }

    get next_step(): any {
        return this.can_advance ? this.STEPS[this.step_index + 1] : null;
    }

    lineTotal(brick: any): number {
        const quantity = Number(brick.quantity || 0);
        const price = Number(brick.price || 0);
        if (quantity && price) return quantity * price;
        return Number(brick.subtotal || 0);
    }

    brickName(brick: any): string {
        if (brick && brick.brick && brick.brick.name) return brick.brick.name;
        return (brick && brick.name) ? brick.name : 'Producto';
    }

    /** Variante elegida en la caja ("Whiskey"); '' si es el original. */
    variantOf(brick: any): string {
        return lineVariant(brick);
    }

    brickImage(brick: any): string {
        const image = brick && brick.brick && brick.brick.image;
        return (image && image.downloadURL) ? image.downloadURL : '';
    }

    isPoints(brick: any): boolean {
        return brick && brick.type === 'club';
    }

    stepState(index: number): string {
        if (this.is_canceled) return 'off';
        if (index < this.step_index) return 'done';
        // Entregada es el final del camino: se marca cumplida, no "en curso".
        if (index === this.step_index) return this.is_delivered ? 'done' : 'current';
        return 'todo';
    }

    // ── Acciones ────────────────────────────────────────────────────────────

    async seeBrick(brick: any) {
        if (!brick || !brick.brick) return;
        const modal = await this.modalController.create({
            component: BrickPreviewComponent,
            cssClass: 'addModal',
            componentProps: { brick: brick.brick }
        });
        return await modal.present();
    }

    /**
     * Avanza al siguiente estado con el mismo servicio que usa el monitor.
     * Para "lista" se manda 'pickup': el servicio lo guarda como 'way' y avisa
     * al cliente que puede pasar por su orden, que es lo que ocurre en la barra.
     */
    async advance() {
        const next = this.next_step;
        if (!next || this.advancing) return;
        this.advancing = true;
        try {
            const target = next.status === 'way' ? 'pickup' : next.status;
            await this.functionsService.changeStatus(this.order, target);
        } catch (e) {
            console.error('No se pudo cambiar el estado:', e);
        }
        this.advancing = false;
    }

    async printTicket() {
        const order = this.order;
        let number = '';
        if (this.zone_name) {
            number = this.zone_name.split(' ').map(word => word.charAt(0).toUpperCase()).join('');
        }
        number += order.number;

        // El ticket imprime los montos tal cual; se mandan ya con dos decimales.
        const products: any[] = this.bricks.map(item => {
            const quantity = Number(item.quantity || 0);
            const price = Number(item.price || 0);
            return {
                // Con su variante, para que en la barra sepan qué servir.
                description: lineName(item),
                quantity: quantity,
                priceUnity: price.toFixed(2),
                totalLine: this.lineTotal(item).toFixed(2)
            };
        });

        await generateTicket('print', {
            date: this.added_date.toLocaleString(),
            boxName: 'Barlleno',
            numberTiket: number,
            billedBy: (this.auth.email || '').split('@')[0],
            products: products,
            total: this.total.toFixed(2),
            typePayment: this.is_courtesy ? `Regalía · ${this.courtesy_company_label}` : this.payment_label,
        });
    }

    closeModal = async () => {
        await this.modalController.dismiss();
    }

    // ── Utilidades ──────────────────────────────────────────────────────────

    private toDate(value: any): Date {
        if (!value) return null;
        if (value instanceof Date) return value;
        if (typeof value.toDate === 'function') return value.toDate();
        const parsed = new Date(value);
        return isNaN(parsed.getTime()) ? null : parsed;
    }

    private firstLine(text: string): string {
        return String(text).split('\n')[0].trim();
    }

    /** Valores de relleno que deja la caja al crear la orden. */
    private isPlaceholder(value: any): boolean {
        const text = String(value || '').trim();
        return !text || text === '000000-0' || text === '7777-7777';
    }
}
